import React, { FunctionComponent } from 'react';
import { ModalScreen } from 'components/modal-screen';
import { ModalType } from 'components/modal-screen/ModalType';
import { InPageDialog } from 'damco-components';
import { RadioButtonList } from 'components/radio-button/list';
import { Input } from 'components/input/IndexAdvanced';
import { IExportExtraDocumentConfiguration, IImportExtraDocumentConfiguration } from 'reducers/extraDocument/models';
import { INickname } from 'reducers/nickname';

interface IAddEditExtraDocConfigDialogProps {
    closeModal: (data?: IExportExtraDocumentConfiguration | IImportExtraDocumentConfiguration) => void;
    exportDataModel: IExportExtraDocumentConfiguration;
    importDataModel: IImportExtraDocumentConfiguration;
    currentUser: INickname;
    extraDocModalError: string;
    initialConfigType: string;
    isBKSFeatureEnabled: boolean;
    isDCSFeatureEnabled: boolean;
    isFinopsAirEnabled: boolean;
    isRailRoadEnabled: boolean;
}

export const AddEditExtraDocConfigDialog: FunctionComponent<IAddEditExtraDocConfigDialogProps> = (props) => {
    const closeModal = () => {
        props.closeModal();
    };
    const [errorMsg, setErrorMsg] = React.useState('');
    const [showErrorDialog, setShowErrorDialog] = React.useState(false);
    const [configType, setConfigType] = React.useState('Export');
    const [shipper, setShipper] = React.useState('');
    const [consignee, setConsignee] = React.useState('');
    const [portOfLoad, setPortOfLoad] = React.useState('');
    const [placeOfReceipt, setPlaceOfReceipt] = React.useState('');
    const [originService, setOriginService] = React.useState('');
    const [destinationService, setDestinationService] = React.useState('');
    const [transportationMode, setTransportationMode] = React.useState('SEA');
    const [productTypeImport, setProductTypeImport] = React.useState('NETWORK');
    const [productTypeExport, setProductTypeExport] = React.useState('NETWORK');
    const [incoTerm, setIncoTerm] = React.useState('');
    const [prefix, setPrefix] = React.useState('');
    const [companyCode, setCompanyCode] = React.useState('');
    const [salesOrganization, setSalesOrganization] = React.useState('');
    const [documentIssuerCountryCode, setDocumentIssuerCountryCode] = React.useState('');
    const [documentIssuerBusinessEntityCode, setDocumentIssuerBusinessEntityCode] = React.useState('');
    const [documentIssuerFunctionCode, setDocumentIssuerFunctionCode] = React.useState('');
    const [division, setDivision] = React.useState('');
    const [salesOffice, setSalesOffice] = React.useState('');
    const [plant, setPlant] = React.useState('');
    const [clientPlaceOfDelivery, setClientPlaceOfDelivery] = React.useState('');
    const [carrierPlaceOfDelivery, setCarrierPlaceOfDelivery] = React.useState('');
    const [portOfDischarge, setPortOfDischarge] = React.useState('');

    const validateAll = (): boolean => {
        const isDCSProduct = productTypeImport === 'DCS';

        const requiedFieldsEmpty = consignee === '' ||
            portOfLoad === '' ||
            placeOfReceipt === '' ||
            originService === '' ||
            destinationService === '' ||
            transportationMode === '' ||
            companyCode === '' ||
            salesOrganization === '' ||
            division === '' ||
            salesOffice === '';

        const dcsRequiedFieldsEmpty = consignee === '' ||
            destinationService === '' ||
            transportationMode === '' ||
            companyCode === '' ||
            salesOrganization === '' ||
            division === '' ||
            salesOffice === '';

        if (isDCSProduct) {
            if (dcsRequiedFieldsEmpty) {
                setShowErrorDialog(true);
                setErrorMsg(`Please review required fields`);
                return false;
            }
            if (portOfLoad !== '' && portOfLoad.length < 5) {
                setShowErrorDialog(true);
                setErrorMsg(`Port of Load shouldn't be less than 5 characters.`);
                return false;
            }
            if (placeOfReceipt !== '' && placeOfReceipt.length < 5) {
                setShowErrorDialog(true);
                setErrorMsg(`Place of Receipt shouldn't be less than 5 characters.`);
                return false;
            }
        } else if (requiedFieldsEmpty) {
            setShowErrorDialog(true);
            setErrorMsg(`Please review required fields`);
            return false;
        }

        if (configType === 'Export' &&
            (shipper === '' ||
                prefix === '' ||
                documentIssuerCountryCode === '' ||
                documentIssuerBusinessEntityCode === '' ||
                documentIssuerFunctionCode === '' || plant === '')
        ) {
            setShowErrorDialog(true);
            setErrorMsg(`Please review required fields`);
            return false;
        }

        if (props.isBKSFeatureEnabled) {
            if ((configType === 'Export' && productTypeExport === '') || (configType === 'Import' && productTypeImport === '')) {
                setShowErrorDialog(true);
                setErrorMsg(`Please review required fields`);
                return false;
            }
        }

        if (configType === 'Import' &&
            (clientPlaceOfDelivery === '' ||
                carrierPlaceOfDelivery === '' ||
                portOfDischarge === '' || plant === '')
        ) {
            setShowErrorDialog(true);
            setErrorMsg(`Please review required fields`);
            return false;
        }

        if (!isDCSProduct && portOfLoad.length < 5) {
            setShowErrorDialog(true);
            setErrorMsg(`Port of Load shouldn't be less than 5 characters.`);
            return false;
        }

        if (!isDCSProduct && placeOfReceipt.length < 5) {
            setShowErrorDialog(true);
            setErrorMsg(`Place of Receipt shouldn't be less than 5 characters.`);
            return false;
        }

        if (configType === 'Import' && clientPlaceOfDelivery.length < 5) {
            setShowErrorDialog(true);
            setErrorMsg(`Client Place of Delivery shouldn't be less than 5 characters.`);
            return false;
        }

        if (configType === 'Import' && carrierPlaceOfDelivery.length < 5) {
            setShowErrorDialog(true);
            setErrorMsg(`Carrier Place of Delivery shouldn't be less than 5 characters.`);
            return false;
        }

        if (configType === 'Import' && portOfDischarge.length < 5) {
            setShowErrorDialog(true);
            setErrorMsg(`Port of Discharge shouldn't be less than 5 characters.`);
            return false;
        }

        setShowErrorDialog(false);
        setErrorMsg(``);
        return true;
    };

    const handleSubmit = () => {
        if (validateAll()) {
            if (configType === 'Export') {
                const isUpdatePopup = props.exportDataModel?.id !== 0;
                const responseObject: IExportExtraDocumentConfiguration = {
                    shipper,
                    consignee,
                    portOfLoad,
                    placeOfReceipt,
                    originService,
                    destinationService,
                    transportationMode,
                    incoTerm,
                    prefix,
                    companyCode,
                    salesOrganization,
                    documentIssuerCountryCode,
                    documentIssuerBusinessEntityCode,
                    documentIssuerFunctionCode,
                    division,
                    salesOffice,
                    plant,
                    productTypeExport,
                    type: configType
                };
                if (isUpdatePopup) {
                    responseObject.isDeleted = props.exportDataModel?.isDeleted;
                    responseObject.id = props.exportDataModel?.id;
                    responseObject.timeStamp = props.exportDataModel?.timeStamp;
                    responseObject.updatedBy = props.currentUser.employeeId;
                }
                else {
                    responseObject.createdBy = props.currentUser.employeeId;
                }
                props.closeModal(responseObject);
            }
            else if (configType === 'Import') {
                const isUpdatePopup = props.importDataModel?.id !== 0;
                const responseObject: IImportExtraDocumentConfiguration = {
                    consignee,
                    portOfLoad,
                    clientPlaceOfDelivery,
                    placeOfReceipt,
                    carrierPlaceOfDelivery,
                    portOfDischarge,
                    originService,
                    destinationService,
                    transportationMode,
                    incoTerm,
                    companyCode,
                    salesOrganization,
                    division,
                    salesOffice,
                    plant,
                    productTypeImport,
                    type: configType
                };
                if (isUpdatePopup) {
                    responseObject.isDeleted = props.importDataModel?.isDeleted;
                    responseObject.id = props.importDataModel?.id;
                    responseObject.timeStamp = props.importDataModel?.timeStamp;
                    responseObject.updatedBy = props.currentUser.employeeId;
                }
                else {
                    responseObject.createdBy = props.currentUser.employeeId;
                }
                props.closeModal(responseObject);
            }
        }
    };

    React.useEffect(() => {
        if (props.extraDocModalError !== '') {
            setShowErrorDialog(true);
            setErrorMsg(props.extraDocModalError);
        }
        else {
            setShowErrorDialog(false);
            setErrorMsg(``);
        }
    }, [props.extraDocModalError]);

    React.useEffect(() => {
        setConfigType(props.initialConfigType);
    }, [props.initialConfigType]);

    React.useEffect(() => {
        if (props.exportDataModel?.id === 0 && props.importDataModel?.id === 0) {
            setConfigType(props.initialConfigType);
            setShipper('');
            setConsignee('');
            setPortOfLoad('');
            setClientPlaceOfDelivery('');
            setPlaceOfReceipt('');
            setCarrierPlaceOfDelivery('');
            setPortOfDischarge('');
            setOriginService('');
            setDestinationService('');
            setTransportationMode('SEA');
            setProductTypeImport('NETWORK');
            setProductTypeExport('NETWORK');
            setIncoTerm('');
            setPrefix('');
            setCompanyCode('');
            setSalesOrganization('');
            setDocumentIssuerCountryCode('');
            setDocumentIssuerBusinessEntityCode('');
            setDocumentIssuerFunctionCode('');
            setDivision('');
            setSalesOffice('');
            setPlant('');
        }
        else {
            if (props.exportDataModel?.id !== 0) {
                setConfigType(props.exportDataModel.type ? props.exportDataModel.type : props.initialConfigType);
                setShipper(props.exportDataModel.shipper ?? '');
                setConsignee(props.exportDataModel.consignee ?? '');
                setPortOfLoad(props.exportDataModel.portOfLoad ?? '');
                setPlaceOfReceipt(props.exportDataModel.placeOfReceipt ?? '');
                setOriginService(props.exportDataModel.originService ?? '');
                setDestinationService(props.exportDataModel.destinationService ?? '');
                setTransportationMode(props.exportDataModel.transportationMode ?? '');
                setProductTypeExport(props.exportDataModel.productType ?? '');
                setPrefix(props.exportDataModel.prefix ?? '');
                setCompanyCode(props.exportDataModel.companyCode ?? '');
                setSalesOrganization(props.exportDataModel.salesOrganization ?? '');
                setDocumentIssuerCountryCode(props.exportDataModel.documentIssuerCountryCode ?? '');
                setDocumentIssuerBusinessEntityCode(props.exportDataModel.documentIssuerBusinessEntityCode ?? '');
                setDocumentIssuerFunctionCode(props.exportDataModel.documentIssuerFunctionCode ?? '');
                setDivision(props.exportDataModel.division ?? '');
                setSalesOffice(props.exportDataModel.salesOffice ?? '');
                setPlant(props.exportDataModel.plant ?? '');
            }
            else if (props.importDataModel?.id !== 0) {
                setConfigType(props.importDataModel.type ? props.importDataModel.type : props.initialConfigType);
                setConsignee(props.importDataModel.consignee ?? '');
                setPortOfLoad(props.importDataModel.portOfLoad ?? '');
                setClientPlaceOfDelivery(props.importDataModel.clientPlaceOfDelivery ?? '');
                setPlaceOfReceipt(props.importDataModel.placeOfReceipt ?? '');
                setCarrierPlaceOfDelivery(props.importDataModel.carrierPlaceOfDelivery ?? '');
                setPortOfDischarge(props.importDataModel.portOfDischarge ?? '');
                setOriginService(props.importDataModel.originService ?? '');
                setDestinationService(props.importDataModel.destinationService ?? '');
                setTransportationMode(props.importDataModel.transportationMode ?? '');
                setProductTypeImport(props.importDataModel.productType ?? '');
                setCompanyCode(props.importDataModel.companyCode ?? '');
                setSalesOrganization(props.importDataModel.salesOrganization ?? '');
                setDivision(props.importDataModel.division ?? '');
                setSalesOffice(props.importDataModel.salesOffice ?? '');
                setPlant(props.importDataModel.plant ?? '');
            }
        }
    }, [props.exportDataModel, props.importDataModel]);

    const importProductTypeOptions = () => {
        return props.isDCSFeatureEnabled ? <>
            <option value="NETWORK">NETWORK</option>
            <option value="BKS">BKS</option>
            <option value="DCS">DCS</option>
        </> : <>
            <option value="NETWORK">NETWORK</option>
            <option value="BKS">BKS</option>
        </>;
    };

    const applyDefaultBksServiceValues = () => {
        const productType = configType === 'Export' ? productTypeExport : productTypeImport;
        const isAirMode = props.isFinopsAirEnabled && transportationMode === 'AIR';
        const isRailOrTruckMode = props.isRailRoadEnabled && (transportationMode === 'RAIL' || transportationMode === 'TRUCK');
        if (productType === 'BKS' && !isAirMode && !isRailOrTruckMode) {
            if (!originService) { setOriginService('CY'); }
            if (!destinationService) { setDestinationService('CY'); }
        }
    };

    return (
        <form onSubmit={handleSubmit}>
            <ModalScreen
                id={'extra-doc-create-new-modal'}
                title={props.exportDataModel?.id ? `Update ID-${props.exportDataModel?.id}` : props.importDataModel?.id ? `Update ID-${props.importDataModel?.id}` : `Create New`}
                visible={true}
                modalType={ModalType.l()}
                closeModal={closeModal}
                hideSecondaryButton={false}
                secondaryButtonTitle="Cancel"
                secondaryButtonFunc={closeModal}
                primaryButtonTitle={props.exportDataModel?.id ? 'Save' : props.importDataModel?.id ? 'Save' : 'Submit'}
                primaryButtonFunc={handleSubmit}
            >
                <InPageDialog
                    showDialog={showErrorDialog}
                    type={'error'}
                    message={errorMsg}
                    closeMethod={() => setShowErrorDialog(false)}
                />
                <div className="grid-wrapper">
                    <div className="col-90">
                        {props.exportDataModel?.id !== 0 || props.importDataModel?.id !== 0
                            ?
                            <Input id={'extraDocumentPopup-configType'}
                                label={'Type of Service'}
                                value={configType}
                                readonly={true} />
                            :
                            <RadioButtonList
                                id={'extraDocumentPopup-configType'}
                                className={'col-70'}
                                label="Type of Service"
                                value={configType}
                                hasError={false}
                                error="Field is required"
                                list={[{
                                    label: 'Export',
                                    name: 'configType',
                                    value: 'Export'
                                },
                                {
                                    label: 'Import',
                                    name: 'configType',
                                    value: 'Import'
                                }]}
                                onChange={(e: any) => { setConfigType(e); setShowErrorDialog(false); }}
                            />}
                    </div>
                    <div className="col-10">
                        <Input id={'extraDocumentPopup-status'}
                            className={props.exportDataModel?.isDeleted ? 'in-active-red' : 'active-green'}
                            label={'Status'}
                            value={props.exportDataModel?.isDeleted ? 'In-Active' : 'Active'}
                            readonly={true} />
                    </div>
                </div>
                <h3 className="title-blue-underline">Document Qualifiers</h3>
                {configType === 'Export' &&
                    <>
                        <div className="grid-wrapper">
                            {props.isBKSFeatureEnabled && <>{props.exportDataModel?.id === 0 ?
                                <div className="col-25">
                                    <div className="form-group">
                                        <label className={showErrorDialog && productTypeExport === '' ? 'field-label-incomplete' : ''}>Product Type</label>
                                        <span className="required-field"> *</span>
                                        <select className="form-control" id={'extraDocumentPopup-productType'} value={productTypeExport} onChange={(e) => { setProductTypeExport(e.target.value); }}>
                                            <option value="NETWORK">NETWORK</option>
                                            <option value="BKS">BKS</option>

                                        </select>
                                    </div>
                                </div> : <Input label={'Product Type'}
                                    className="col-25"
                                    id="chargeItem"
                                    value={props.exportDataModel.productType} type="text" readonly={true} />}</>}
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && shipper === '' ? 'field-label-incomplete' : ''}>Shipper</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-shipper'} type="text" value={shipper} onChange={(e) => { setShipper(e.target.value); }} />
                                </div>
                            </div>
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && consignee === '' ? 'field-label-incomplete' : ''}>Consignee</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-consignee'} type="text" value={consignee} onChange={(e) => { setConsignee(e.target.value); }} />
                                </div>
                            </div>
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && portOfLoad.length < 5 ? 'field-label-incomplete' : ''}>Port of Load</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-pol'} type="text" value={portOfLoad} onChange={(e) => { setPortOfLoad(e.target.value); }} />
                                </div>
                            </div>

                        </div>
                        <div className="grid-wrapper">
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && placeOfReceipt.length < 5 ? 'field-label-incomplete' : ''}>Place of Receipt</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-placeOfReceipt'} type="text" value={placeOfReceipt} onChange={(e) => { setPlaceOfReceipt(e.target.value); }} />
                                </div>
                            </div>
                            {applyDefaultBksServiceValues()}
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && originService === '' ? 'field-label-incomplete' : ''}>Origin Service</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-originService'} type="text" value={originService} onChange={(e) => { setOriginService(e.target.value); }} />
                                </div>
                            </div>
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && destinationService === '' ? 'field-label-incomplete' : ''}>Destination Service</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-destinationService'} type="text" value={destinationService} onChange={(e) => { setDestinationService(e.target.value); }} />
                                </div>
                            </div>
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && transportationMode === '' ? 'field-label-incomplete' : ''}>Transportation Mode</label>
                                    <span className="required-field"> *</span>
                                    <select className="form-control" id={'extraDocumentPopup-transportationMode'} value={transportationMode} onChange={(e) => { setTransportationMode(e.target.value); }}>
                                        <option value="SEA">SEA</option>
                                        <option value="RAIL">RAIL</option>
                                        <option value="AIR">AIR</option>
                                        <option value="OCEAN">OCEAN</option>
                                        <option value="TRUCK">TRUCK</option>
                                    </select>
                                </div>
                            </div>

                        </div>
                        <div className="grid-wrapper">
                            <div className="col-25">
                                <div className="form-group">
                                    <label>Inco Term</label>
                                    <input className="form-control" id={'extraDocumentPopup-incoterm'} type="text" value={incoTerm} onChange={(e) => { setIncoTerm(e.target.value); }} />
                                </div>
                            </div>
                        </div>

                        <h3 className="title-blue-underline">Second Document Values</h3>
                        <div className="grid-wrapper">
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && prefix === '' ? 'field-label-incomplete' : ''}>PREFIX</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-prefix'} type="text" value={prefix} onChange={(e) => { setPrefix(e.target.value); }} />
                                </div>
                            </div>
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && companyCode === '' ? 'field-label-incomplete' : ''}>Company Code</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-companyCode'} type="text" value={companyCode} onChange={(e) => { setCompanyCode(e.target.value); }} />
                                </div>
                            </div>
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && salesOrganization === '' ? 'field-label-incomplete' : ''}>Sales Org</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-salesOrg'} type="text" value={salesOrganization} onChange={(e) => { setSalesOrganization(e.target.value); }} />
                                </div>
                            </div>
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && division === '' ? 'field-label-incomplete' : ''}>Division</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-division'} type="text" value={division} onChange={(e) => { setDivision(e.target.value); }} />
                                </div>
                            </div>
                        </div>
                        <div className="grid-wrapper">
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && documentIssuerCountryCode === '' ? 'field-label-incomplete' : ''}>Document Issuer Country Code</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-documentIssuerCountryCode'} type="text" value={documentIssuerCountryCode} onChange={(e) => { setDocumentIssuerCountryCode(e.target.value); }} />
                                </div>
                            </div>
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && documentIssuerBusinessEntityCode === '' ? 'field-label-incomplete' : ''}>Document Issuer Business Entity Code</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-documentIssuerBusinessEntityCode'} type="text" value={documentIssuerBusinessEntityCode} onChange={(e) => { setDocumentIssuerBusinessEntityCode(e.target.value); }} />
                                </div>
                            </div>
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && documentIssuerFunctionCode === '' ? 'field-label-incomplete' : ''}>Document Issuer Function Code</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-documentIssuerFunctionCode'} type="text" value={documentIssuerFunctionCode} onChange={(e) => { setDocumentIssuerFunctionCode(e.target.value); }} />
                                </div>
                            </div>
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && salesOffice === '' ? 'field-label-incomplete' : ''}>Sales Office</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-salesOffice'} type="text" value={salesOffice} onChange={(e) => { setSalesOffice(e.target.value); }} />
                                </div>
                            </div>
                        </div>
                        <div className="grid-wrapper">
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && plant === '' ? 'field-label-incomplete' : ''}>Plant</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-plant'} type="text" value={plant} onChange={(e) => { setPlant(e.target.value); }} />
                                </div>
                            </div>
                        </div>
                    </>
                }

                {configType === 'Import' &&
                    <>
                        <div className="grid-wrapper">
                            {props.isBKSFeatureEnabled && <>{props.importDataModel?.id === 0 ?
                                <div className="col-25">
                                    <div className="form-group">
                                        <label className={showErrorDialog && productTypeImport === '' ? 'field-label-incomplete' : ''}>Product Type</label>
                                        <span className="required-field"> *</span>
                                        <select className="form-control" id={'extraDocumentPopup-productType'} value={productTypeImport} onChange={(e) => { setProductTypeImport(e.target.value); }}>
                                            {importProductTypeOptions()}
                                        </select>
                                    </div>
                                </div> : <Input label={'Product Type'}
                                    className="col-25"
                                    id="chargeItem"
                                    value={props.importDataModel.productType} type="text" readonly={true} />}</>}
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && consignee === '' ? 'field-label-incomplete' : ''}>Consignee</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-consignee'} type="text" value={consignee} onChange={(e) => { setConsignee(e.target.value); }} />
                                </div>
                            </div>
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={productTypeImport !== 'DCS' && showErrorDialog && portOfLoad.length < 5 ? 'field-label-incomplete' : ''}>Port of Load</label>
                                    {productTypeImport !== 'DCS' && <span className="required-field"> *</span>}
                                    <input className="form-control" id={'extraDocumentPopup-pol'} type="text" value={portOfLoad} onChange={(e) => { setPortOfLoad(e.target.value); }} />
                                </div>
                            </div>
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && clientPlaceOfDelivery.length < 5 ? 'field-label-incomplete' : ''}>Client Place of Delivery</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-clpod'} type="text" value={clientPlaceOfDelivery} onChange={(e) => { setClientPlaceOfDelivery(e.target.value); }} />
                                </div>
                            </div>
                        </div>
                        <div className="grid-wrapper">
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={productTypeImport !== 'DCS' && showErrorDialog && placeOfReceipt.length < 5 ? 'field-label-incomplete' : ''}>Place of Receipt</label>
                                    {productTypeImport !== 'DCS' && <span className="required-field"> *</span>}
                                    <input className="form-control" id={'extraDocumentPopup-placeOfReceipt'} type="text" value={placeOfReceipt} onChange={(e) => { setPlaceOfReceipt(e.target.value); }} />
                                </div>
                            </div>
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && carrierPlaceOfDelivery.length < 5 ? 'field-label-incomplete' : ''}>Carrier Place of Delivery</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-carrierPlaceOfDelivery'} type="text" value={carrierPlaceOfDelivery} onChange={(e) => { setCarrierPlaceOfDelivery(e.target.value); }} />
                                </div>
                            </div>
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && portOfDischarge.length < 5 ? 'field-label-incomplete' : ''}>Port of Discharge</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-portOfDischarge'} type="text" value={portOfDischarge} onChange={(e) => { setPortOfDischarge(e.target.value); }} />
                                </div>
                            </div>
                            {applyDefaultBksServiceValues()}
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={productTypeImport !== 'DCS' && showErrorDialog && originService === '' ? 'field-label-incomplete' : ''}>Origin Service</label>
                                    {productTypeImport !== 'DCS' && <span className="required-field"> *</span>}
                                    <input className="form-control" id={'extraDocumentPopup-originService'} type="text" value={originService} onChange={(e) => { setOriginService(e.target.value); }} />
                                </div>
                            </div>
                        </div>
                        <div className="grid-wrapper">
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && destinationService === '' ? 'field-label-incomplete' : ''}>Destination Service</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-destinationService'} type="text" value={destinationService} onChange={(e) => { setDestinationService(e.target.value); }} />
                                </div>
                            </div>
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && transportationMode === '' ? 'field-label-incomplete' : ''}>Transportation Mode</label>
                                    <span className="required-field"> *</span>
                                    <select className="form-control" id={'extraDocumentPopup-transportationMode'} value={transportationMode} onChange={(e) => { setTransportationMode(e.target.value); }}>
                                        <option value="SEA">SEA</option>
                                        <option value="RAIL">RAIL</option>
                                        <option value="AIR">AIR</option>
                                        <option value="OCEAN">OCEAN</option>
                                        <option value="TRUCK">TRUCK</option>
                                    </select>
                                </div>
                            </div>
                            <div className="col-25">
                                <div className="form-group">
                                    <label>Inco Term</label>
                                    <input className="form-control" id={'extraDocumentPopup-incoterm'} type="text" value={incoTerm} onChange={(e) => { setIncoTerm(e.target.value); }} />
                                </div>
                            </div>
                        </div>
                        <h3 className="title-blue-underline">Second Document Values</h3>
                        <div className="grid-wrapper">
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && companyCode === '' ? 'field-label-incomplete' : ''}>Company Code</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-companyCode'} type="text" value={companyCode} onChange={(e) => { setCompanyCode(e.target.value); }} />
                                </div>
                            </div>
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && salesOrganization === '' ? 'field-label-incomplete' : ''}>Sales Org</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-salesOrg'} type="text" value={salesOrganization} onChange={(e) => { setSalesOrganization(e.target.value); }} />
                                </div>
                            </div>
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && division === '' ? 'field-label-incomplete' : ''}>Division</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-division'} type="text" value={division} onChange={(e) => { setDivision(e.target.value); }} />
                                </div>
                            </div>
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && salesOffice === '' ? 'field-label-incomplete' : ''}>Sales Office</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-salesOffice'} type="text" value={salesOffice} onChange={(e) => { setSalesOffice(e.target.value); }} />
                                </div>
                            </div>
                        </div>
                        <div className="grid-wrapper">
                            <div className="col-25">
                                <div className="form-group">
                                    <label className={showErrorDialog && plant === '' ? 'field-label-incomplete' : ''}>Plant</label>
                                    <span className="required-field"> *</span>
                                    <input className="form-control" id={'extraDocumentPopup-plant'} type="text" value={plant} onChange={(e) => { setPlant(e.target.value); }} />
                                </div>
                            </div>
                        </div>
                    </>
                }
                {configType === 'Export' && props.exportDataModel?.id || configType === 'Import' && props.importDataModel?.id ?
                    <>
                        <h3 className="title-blue-underline">Other Details</h3>
                        <div className="grid-wrapper">
                            <div className="col-25">
                                <Input id={'extraDocumentPopup-createdBy'}
                                    label={'Created By'}
                                    value={configType === 'Export' ? props.exportDataModel?.createdBy : props.importDataModel?.createdBy}
                                    readonly={true} />
                            </div>
                            <div className="col-25">
                                <Input id={'extraDocumentPopup-createdOn'}
                                    label={'Created On'}
                                    value={configType === 'Export' ? props.exportDataModel?.createdOn : props.importDataModel?.createdOn}
                                    readonly={true} />
                            </div>
                            <div className="col-25">
                                <Input id={'extraDocumentPopup-updatedBy'}
                                    label={'Updated By'}
                                    value={configType === 'Export' ? props.exportDataModel?.updatedBy : props.importDataModel?.updatedBy}
                                    readonly={true} />
                            </div>
                            <div className="col-25">
                                <Input id={'extraDocumentPopup-updatedOn'}
                                    label={'Updated On'}
                                    value={configType === 'Export' ? props.exportDataModel?.updatedOn : props.importDataModel?.updatedOn}
                                    readonly={true} />
                            </div>
                        </div>
                    </> : ''
                }
            </ModalScreen>
        </form>
    );
};